import { useState, useMemo } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import BankGradeCard from "./BankGradeCard";

export default function LoanCalculator() {
  const [loanAmount, setLoanAmount] = useState(30000);
  const [interestRate, setInterestRate] = useState(1);
  const [loanTerm, setLoanTerm] = useState(30);

  const { dailyPayment, totalPayment, totalInterest } = useMemo(() => {
    const principal = loanAmount;

    const dailyInterest = (principal * interestRate) / 100;
    const totalInterest = dailyInterest * loanTerm;
    const totalPayment = principal + totalInterest;
    const dailyPayment = totalPayment / loanTerm;

    return { dailyPayment, totalPayment, totalInterest };
  }, [loanAmount, interestRate, loanTerm]);

  return (
    <>
      <div className="min-w-screen bg-[#141237]">
        <div className="flex w-full flex-col items-center justify-center gap-12 px-4 py-16">
          <Navbar />

          {/* Hero Section */}
          <div className="flex flex-col items-center gap-1 overflow-hidden rounded-md bg-gradient-to-br from-pink-600 via-red-500 to-pink-700 px-4 sm:px-6 py-10 sm:py-16">
            <div className="flex w-full flex-col items-center gap-10 rounded-2xl bg-white px-4 sm:px-8 py-10 sm:py-14 shadow-xl  backdrop-blur-sm">
              {/* Title Section */}
              <div className="flex flex-col items-center gap-3 text-center w-full">
                <h2 className="text-3xl sm:text-4xl lg:text-[42px] font-bold text-[#152693] leading-snug">
                  Loan Calculator
                </h2>
                <p className="text-gray-600 text-base sm:text-lg max-w-[600px]">
                  Adjust the sliders to see your daily EMI, total payment, and
                  interest instantly.
                </p>
              </div>

              {/* Range Inputs */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 w-full">
                {/* Loan Amount */}
                <div className="flex flex-col items-center w-full">
                  <label className="font-medium text-[#152693] mb-2">
                    Loan Amount (₹{loanAmount.toLocaleString()})
                  </label>
                  <input
                    type="range"
                    min={5000}
                    max={100000}
                    step={5000}
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Number(e.target.value))}
                    className="w-full accent-pink-600 cursor-pointer"
                  />
                </div>

                {/* Interest Rate */}
                <div className="flex flex-col items-center w-full">
                  <label className="font-medium text-[#152693] mb-2">
                    Interest Rate ({interestRate}%)
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={2}
                    step={0.5}
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    className="w-full accent-pink-600 cursor-pointer"
                  />
                </div>

                {/* Loan Term (Days) */}
                <div className="flex flex-col items-center w-full">
                  <label className="font-medium text-[#152693] mb-2">
                    Loan Term ({loanTerm} Days)
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={90}
                    step={1}
                    value={loanTerm}
                    onChange={(e) => setLoanTerm(Number(e.target.value))}
                    className="w-full accent-pink-600 cursor-pointer"
                  />
                </div>
              </div>

              {/* Results Section */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 w-full mt-8">
                <div className="flex flex-col items-center gap-2 rounded-xl bg-gray-50 px-6 py-8 text-center shadow-md min-h-[140px]">
                  <span className="text-lg font-semibold text-[#152693]">
                    Daily Payment
                  </span>
                  <span className="text-2xl sm:text-3xl font-bold text-pink-600 tabular-nums">
                    ₹{dailyPayment.toFixed(2)}
                  </span>
                </div>

                <div className="flex flex-col items-center gap-2 rounded-xl bg-gray-50 px-6 py-8 text-center shadow-md min-h-[140px]">
                  <span className="text-lg font-semibold text-[#152693]">
                    Total Payment
                  </span>
                  <span className="text-2xl sm:text-3xl font-bold text-pink-600 tabular-nums">
                    ₹{totalPayment.toFixed(2)}
                  </span>
                </div>

                <div className="flex flex-col items-center gap-2 rounded-xl bg-gray-50 px-6 py-8 text-center shadow-md min-h-[140px]">
                  <span className="text-lg font-semibold text-[#152693]">
                    Total Interest
                  </span>
                  <span className="text-2xl sm:text-3xl font-bold text-pink-600 tabular-nums">
                    ₹{totalInterest.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BankGradeCard />
      <Footer />
    </>
  );
}
